import { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Control } from 'react-hook-form';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import instance from '../../../../api/axios/utils.ts/instance';
import { check14UnderAge } from '../../../../api/utils/util-func';
import { UserSnsType } from '../../../../enum';
import { SignupContext } from '../SignupContext';
import { SIGNUP_MESSAGES } from '../constants';
import { UserDTO } from '../types';

export const useSignupSubmit = (
  checkDuplicate: boolean,
  control: Control<UserDTO>,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { phone, step, setStep } = useContext(SignupContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const sns_code = searchParams.get('sns_code');
  const sns_type = searchParams.get('sns_type');

  const alert = (message: string) => dispatch(openAlert({ message }));

  const postSignUp = async (data: UserDTO) => {
    const response = await instance.post('/v1/auth/sign-up', data);
    const result = response.data;

    if (result.statusCode !== 200) {
      throw new Error(result.message ?? SIGNUP_MESSAGES.SIGNUP_FAILED);
    }
  };

  const onSubmit = async (data: UserDTO) => {
    if (!checkDuplicate) {
      alert(SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED);
      control.setError('uid', {
        message: SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED,
      });
      return;
    }

    if (check14UnderAge(data.birthday)) {
      if (!data.guardianInfo) {
        alert(SIGNUP_MESSAGES.GUARDIAN_INFO_REQUIRED);
        return;
      } else if (
        !data.guardianInfo.name ||
        !data.guardianInfo.gender ||
        !data.guardianInfo.birthday
      ) {
        alert(SIGNUP_MESSAGES.GUARDIAN_INFO_REQUIRED);
        return;
      } else if (!data.guardianInfo.phone) {
        alert(SIGNUP_MESSAGES.GUARDIAN_PHONE_AUTH_REQUIRED);
        return;
      }
    }

    //한글 2글자 이상 정규식
    const regName = /^[가-힣]{2,}$/;
    if (!regName.test(data.guardianInfo.name)) {
      dispatch(
        openAlert({ message: '보호자명은 한글로 2자 이상 입력해주세요.' }),
      );
      return;
    }

    // 보호자 연락처 유효성 검사
    const regPhone = /^0(?:2|[0-9]{2})(?:[0-9]{3,4})[0-9]{4}$/;
    if (!regPhone.test(data.guardianInfo.phone.replace(/-/g, ''))) {
      dispatch(
        openAlert({
          message:
            '보호자 연락처의 전화번호 형식이 올바르지 않습니다. (예: 01012345678)',
        }),
      );
      return;
    }

    const packet: UserDTO = {
      ...data,
      countryCode: '82',
      phone: phone,
      agreementLocationalInfo: true,
      agreementPersonalInfo: true,
      agreementServiceUsage: true,
      guardianInfo: check14UnderAge(data.birthday)
        ? data.guardianInfo
        : undefined,
    };

    if (sns_code && sns_type) {
      packet.snsCode = sns_code;
      packet.snsType = sns_type as UserSnsType;
    } else {
      packet.snsType = UserSnsType.DEFAULT;
    }

    try {
      setIsLoading(true);
      await postSignUp(packet);
      setStep(step + 1);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(SIGNUP_MESSAGES.SIGNUP_FAILED);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    onSubmit,
  };
};
